import React, { useEffect, useState, useMemo } from "react";
import { Table, Select, Button, Space, Modal, Avatar, Tooltip, Tag, Badge, Descriptions, Input } from "antd";

import { UserOutlined, BankOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../../../core/data/redux/store";
import { selectActiveCompany, selectLeadForms } from "../../../../../core/data/redux/leads-queue/selectors";
import { fetchLeadBankData, assignLeadBank } from "../../../../../core/data/redux/leads-queue/thunks";
import { notify } from "../../../../../services/toast.config";
import type { Employee, LeadBankFilters, LeadBankForm, LeadBankQuery, Pagination } from "../../../../../core/data/redux/leads-queue/types";
import { humanReadableDate } from "../../../../../helper/dateTimeHelper";
import { WhatsAppConversationDrawer } from "../../../../../core/common/whatsapp-conversation/WhatsAppConversationDrawer";
import { ALL_FORMS_LABEL, ALL_FORMS_VALUE } from "../../../../../core/data/redux/leads-queue/constants";

const { Option } = Select;

const SOURCE_FILTER_OPTIONS = [
    { label: "All Sources", value: "" },
    { label: <span><i className="ti ti-brand-facebook me-1" />Facebook</span>, value: "Facebook" },
    { label: <span><i className="ti ti-brand-whatsapp me-1" />WhatsApp</span>, value: "WhatsApp" },
];

const RAW_PREVIEW_COUNT = 2;

const toRawEntries = (response: any): { key: string; value: string }[] => {
    if (!response) return [];
    if (Array.isArray(response)) {
        return response.map((field: any) => ({
            key: field.name,
            value: Array.isArray(field.values) ? field.values.join(", ") : String(field.values ?? "—"),
        }));
    }
    return Object.entries(response).map(([k, v]) => ({
        key: k,
        value: Array.isArray(v) ? v.join(", ") : String(v ?? "—"),
    }));
};

const DEFAULT_PAGINATION: Pagination = { total: 0, current_page: 1, per_page: 30, total_pages: 0 };

interface LeadBankLead {
    id: number;
    LeadName: string;
    Phone: string;
    Email: string;
    facebook_form_id: string;
    facebook_form_name: string;
    response: Record<string, any> | any[];
    source_details?: Record<string, any>;
    CreatedDate: string;
    lead_source?: string;
    lead_source_child?: string;
}

interface LeadBankTabProps {
    employees: Employee[];
    bankForms: LeadBankForm[];
    filters: LeadBankFilters | null;
    query: LeadBankQuery | null;
    onFiltersChange: (filters: LeadBankFilters) => void;
    onRefreshForms: () => void;
}

const LeadBankTab: React.FC<LeadBankTabProps> = ({ employees, bankForms, filters, query, onFiltersChange, onRefreshForms }) => {
    const dispatch = useDispatch<AppDispatch>();
    const activeCompany = useSelector(selectActiveCompany);
    const leadForms = useSelector(selectLeadForms);

    const [leads, setLeads] = useState<LeadBankLead[]>([]);
    const [pagination, setPagination] = useState<Pagination>(DEFAULT_PAGINATION);
    const [loading, setLoading] = useState(false);
    const [assigning, setAssigning] = useState(false);

    const [tempSearch, setTempSearch] = useState("");
    const [rawDataLead, setRawDataLead] = useState<LeadBankLead | null>(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [assignUserId, setAssignUserId] = useState<string | null>(null);
    const [assignCrmFormId, setAssignCrmFormId] = useState<number | null>(null);
    const [assignedLeadType] = useState<string>("Manual");
    const [conversationLead, setConversationLead] = useState<LeadBankLead | null>(null);

    const sourceFilter = filters?.sourceFilter ?? "";

    const loadLeads = async (page = 1) => {
        if (!activeCompany.company_id || !query) return;
        setLoading(true);
        try {
            const result = await dispatch(fetchLeadBankData({
                companyId: activeCompany.company_id,
                companyType: activeCompany.company_type,
                facebookFormId: query.facebookFormId,
                leadSourceChild: query.leadSourceChild,
                search: query.search,
                page,
                limit: 30,
            })).unwrap();
            setLeads(result.leads || []);
            setPagination(result.pagination || DEFAULT_PAGINATION);
        } catch (err: any) {
            console.error("[LEAD BANK] Fetch error:", err);
            notify.error("Failed to load Lead Bank leads");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setSelectedRowKeys([]);
        if (!filters) {
            setLeads([]);
            setPagination(DEFAULT_PAGINATION);
            setTempSearch("");
            return;
        }
        if (activeCompany.company_id) {
            loadLeads(1);
        }
    }, [activeCompany.company_id, activeCompany.company_type, filters]);

    const handleAssign = () => {
        if (selectedRowKeys.length === 0 || !assignCrmFormId) {
            notify.error("Please select leads and a CRM form");
            return;
        }

        Modal.confirm({
            title: "Assign Lead Bank Leads",
            content: (
                <div>
                    <p>You are about to assign <strong>{selectedRowKeys.length}</strong> lead(s) from the Lead Bank.</p>
                    <p className="text-muted mb-0">
                        {assignUserId
                            ? "They will be mapped to the selected CRM form and assigned to the chosen employee."
                            : "They will be mapped to the selected CRM form and will remain unassigned in the queue."}
                    </p>
                </div>
            ),
            okText: "Yes, Assign",
            cancelText: "Cancel",
            centered: true,
            onOk: async () => {
                setAssigning(true);
                try {
                    await dispatch(assignLeadBank({
                        leadIds: selectedRowKeys,
                        userId: assignedLeadType === "Automatic" ? null : assignUserId,
                        crmFormId: assignCrmFormId,
                        companyId: activeCompany.company_id,
                        companyType: activeCompany.company_type,
                        assignedLeadType: "Manual",
                    })).unwrap();
                    notify.success(`${selectedRowKeys.length} lead(s) assigned successfully`);
                    setSelectedRowKeys([]);
                    setAssignUserId(null);
                    setAssignCrmFormId(null);
                    loadLeads(pagination.current_page);
                    onRefreshForms();
                } catch {
                    notify.error("Failed to assign leads");
                } finally {
                    setAssigning(false);
                }
            },
        });
    };

    const employeeOptions = useMemo(() => {
        return employees?.map((u: any) => ({
            label: u.full_name || `${u.fname} ${u.lname || ""}`.trim(),
            value: u.id.toString(),
            image_url: u?.image_url || "assets/img/users/user.png",
        })) || [];
    }, [employees]);

    const crmFormOptions = useMemo(() => {
        return leadForms?.map((f: any) => ({ label: f.name, value: f.id })) || [];
    }, [leadForms]);

    // ✅ Strict filtering — only show employees allowed for the selected CRM form
    const filteredEmployeeOptions = useMemo(() => {
        if (!assignCrmFormId || !leadForms) return [];

        const selectedForm = leadForms?.find((f: any) => f.id === assignCrmFormId) as any;
        const allowedIds: number[] = selectedForm?.employees ?? [];

        return employeeOptions.filter((opt) => allowedIds.includes(Number(opt.value)));
    }, [assignCrmFormId, employeeOptions, leadForms]);

    const fbFormOptions = useMemo(() => {
        if (bankForms.length === 0) return [];
        return [
            ...bankForms.map((f) => ({
                label: `${f.form_name || f.form_id} (${f.lead_count})`,
                value: f.form_id,
            })),
            { label: ALL_FORMS_LABEL, value: ALL_FORMS_VALUE },
        ];
    }, [bankForms]);

    const handleFiltersChange = (changes: Partial<LeadBankFilters>) => {
        if (!filters) return;
        onFiltersChange({ ...filters, ...changes });
    };

    const renderRawData = (record: LeadBankLead) => {
        const entries = toRawEntries(record.response);
        if (entries.length === 0) return <span className="text-muted">—</span>;
        const hidden = entries.length - RAW_PREVIEW_COUNT;

        return (
            <div className="d-flex flex-wrap align-items-center gap-1">
                {entries.slice(0, RAW_PREVIEW_COUNT).map(({ key, value }) => (
                    <Tag key={key} color="geekblue" style={{ marginBottom: 2 }}>
                        <span className="fw-medium">{key}:</span> {value}
                    </Tag>
                ))}
                <Tooltip title="View all fields">
                    <Tag
                        color="purple"
                        style={{ cursor: "pointer", marginBottom: 2 }}
                        onClick={() => setRawDataLead(record)}
                    >
                        {hidden > 0 ? `+${hidden}` : <i className="ti ti-eye" />}
                    </Tag>
                </Tooltip>
            </div>
        );
    };

    const columns: ColumnsType<LeadBankLead> = [
        {
            title: "Lead Name",
            dataIndex: "LeadName",
            key: "LeadName",
            render: (text: string) => <span className="fw-semibold">{text || "N/A"}</span>,
        },
        {
            title: () => {
                const firstSource = leads[0]?.lead_source;
                return <span>{firstSource || "Lead Source"}</span>;
            },
            key: "source",
            render: (_: any, record: LeadBankLead) => {
                const isFacebook =
                    record.lead_source_child?.toLowerCase() === "facebook";
                const isWhatsApp =
                    record.lead_source_child?.toLowerCase() === "whatsapp";

                const childColor = isFacebook
                    ? "blue"
                    : isWhatsApp
                        ? "green"
                        : "default";

                const childIcon = isFacebook ? (
                    <i className="ti ti-brand-facebook me-1" />
                ) : isWhatsApp ? (
                    <i className="ti ti-brand-whatsapp me-1" />
                ) : null;

                return (
                    <div className="d-flex flex-column gap-1">
                        {/* Lead Source Child */}
                        {record.lead_source_child && (
                            <Tag color={childColor} style={{ width: "fit-content" }}>
                                {childIcon}
                                {record.lead_source_child}
                            </Tag>
                        )}

                        {/* Facebook-specific: form name + id */}
                        {isFacebook && record.facebook_form_name && (
                            <div className="mt-1">
                                <div className="fw-medium small">{record.facebook_form_name}</div>
                                <div className="text-muted" style={{ fontSize: 10 }}>
                                    {record.facebook_form_id}
                                </div>
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            title: "Raw Lead Data",
            dataIndex: "response",
            key: "response",
            render: (_: any, record: LeadBankLead) => renderRawData(record),
        },
        {
            title: "Conversation",
            key: "conversation",
            width: 130,
            render: (_: any, record: LeadBankLead) =>
                record.source_details?.whatsapp_conversation_id ? (
                    <Button
                        size="small"
                        icon={<i className="ti ti-brand-whatsapp" />}
                        style={{ color: "#16a34a", borderColor: "#86efac" }}
                        onClick={() => setConversationLead(record)}
                    >
                        View Chat
                    </Button>
                ) : (
                    <span className="text-muted">—</span>
                ),
        },
        {
            title: "Fetched At",
            dataIndex: "source_details",
            key: "fetched_at",
            sorter: (a, b) =>
                new Date(a.source_details?.fetched_at ?? a.CreatedDate).getTime() -
                new Date(b.source_details?.fetched_at ?? b.CreatedDate).getTime(),
            render: (_: any, record: LeadBankLead) => {
                const date = record.source_details?.fetched_at ?? record.CreatedDate;
                return date ? (
                    <Tooltip title={humanReadableDate(date, true, undefined, undefined, false)}>
                        <span className="text-muted small">
                            <i className="ti ti-calendar me-1" />
                            {humanReadableDate(date, true, undefined, undefined, false)}
                        </span>
                    </Tooltip>
                ) : (
                    <span className="text-muted">—</span>
                );
            },
        },
    ];

    return (
        <div className="card">
            {/* Header */}
            <div className="card-header border-0 pb-0">
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="d-flex align-items-center gap-2">
                        <BankOutlined style={{ fontSize: 28, color: "#6366f1" }} />
                        <div>
                            <h4 className="mb-0">Lead Bank</h4>
                            <p className="text-muted mb-0 small">
                                Leads synced from Facebook and WhatsApp without CRM mapping — review and assign them below
                            </p>
                        </div>
                    </div>
                    <Badge
                        count={pagination.total}
                        overflowCount={9999}
                        showZero
                        style={{ backgroundColor: "#6366f1", fontSize: 12 }}
                    />
                </div>
            </div>

            {/* Filters & Actions */}
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                <div className="d-flex gap-2 align-items-center flex-wrap">
                    <Select
                        style={{ minWidth: 160 }}
                        options={SOURCE_FILTER_OPTIONS}
                        value={sourceFilter}
                        disabled={!filters}
                        onChange={(v) => handleFiltersChange({ sourceFilter: String(v) })}
                    />
                    {sourceFilter !== "WhatsApp" && fbFormOptions.length > 0 && (
                    <Select
                        style={{ minWidth: 240 }}
                        placeholder="Select Facebook Form"
                        value={filters?.facebookFormId}
                        disabled={!filters}
                        onChange={(v) => handleFiltersChange({ facebookFormId: v })}
                        options={fbFormOptions}
                        showSearch
                        filterOption={(input, opt: any) =>
                            String(opt?.label ?? "").toLowerCase().includes(input.toLowerCase())
                        }
                    />
                    )}
                    <Input.Search
                        style={{ width: 260 }}
                        placeholder="Search name, phone or email"
                        allowClear
                        value={tempSearch}
                        disabled={!filters}
                        onChange={(e) => {
                            setTempSearch(e.target.value);
                            if (!e.target.value && filters?.search) handleFiltersChange({ search: "" });
                        }}
                        onSearch={(value) => handleFiltersChange({ search: value.trim() })}
                    />
                    <Button
                        icon={<i className="ti ti-refresh" />}
                        onClick={() => {
                            loadLeads(1);
                            onRefreshForms();
                        }}
                        loading={loading}
                    >
                        Refresh
                    </Button>
                </div>

                {/* Assignment Controls — shown only when rows are selected */}
                {selectedRowKeys.length > 0 && (
                    <Space wrap>
                        <Select
                            style={{ width: 200 }}
                            placeholder="Select CRM Form"
                            value={assignCrmFormId ?? undefined}
                            onChange={(v) => {
                                setAssignCrmFormId(v ?? null);
                                setAssignUserId(null);
                            }}
                            allowClear
                            showSearch
                            options={crmFormOptions}
                            filterOption={(input, opt: any) =>
                                (opt?.label ?? "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                        <Select
                            style={{ width: 210 }}
                            placeholder="Select Employee"
                            value={assignUserId ?? undefined}
                            onChange={(v) => setAssignUserId(v ?? null)}
                            allowClear
                            showSearch
                            filterOption={(input, opt: any) =>
                                (opt?.label ?? "").toLowerCase().includes(input.toLowerCase())
                            }
                            options={filteredEmployeeOptions}
                            optionRender={(opt) => (
                                <Space>
                                    <Avatar
                                        size="small"
                                        src={(opt.data as any)?.image_url}
                                        icon={<UserOutlined />}
                                    />
                                    <span>{opt.label}</span>
                                </Space>
                            )}
                        />
                        <Button
                            type="primary"
                            disabled={!assignCrmFormId || assigning}
                            loading={assigning}
                            onClick={handleAssign}
                            style={{ backgroundColor: "#6366f1", border: "none" }}
                        >
                            Assign Selected ({selectedRowKeys.length})
                        </Button>
                    </Space>
                )}
            </div>

            {/* Table */}
            <div className="card-body p-0 mb-3">
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }}>
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3 text-muted">Loading Lead Bank...</p>
                        </div>
                    </div>
                ) : leads.length > 0 ? (
                    <Table
                        className="datanew dataTable no-footer"
                        dataSource={leads}
                        columns={columns}
                        rowKey="id"
                        rowSelection={{
                            selectedRowKeys,
                            onChange: (keys) => setSelectedRowKeys(keys),
                        }}
                        pagination={{
                            current: pagination.current_page,
                            pageSize: pagination.per_page,
                            total: pagination.total,
                            onChange: (page) => loadLeads(page),
                            showTotal: (total, range) => `Showing ${range[0]} - ${range[1]} of ${total} entries`,
                            showSizeChanger: false,
                        }}
                        scroll={{ x: 900 }}
                    />
                ) : (
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
                        <div className="text-center">
                            <BankOutlined style={{ fontSize: "4rem", color: "#ccc" }} />
                            <h5 className="mt-3 mb-2 fw-semibold">
                                {filters?.search ? "No Matching Leads" : "Lead Bank is Empty"}
                            </h5>
                            <p className="text-muted mb-0">
                                {filters?.search
                                    ? `No Lead Bank leads match "${filters.search}" for the selected filters.`
                                    : "No leads are waiting in the bank. Sync a Facebook form to \"Lead Bank\" to start capturing leads here."}
                            </p>
                        </div>
                    </div>
                )}
            </div>

            <Modal
                open={Boolean(rawDataLead)}
                onCancel={() => setRawDataLead(null)}
                footer={null}
                centered
                width={620}
                title={
                    <span>
                        Raw Lead Data · {rawDataLead?.LeadName || "N/A"}
                        {rawDataLead?.lead_source_child && (
                            <Tag className="ms-2" color={rawDataLead.lead_source_child.toLowerCase() === "whatsapp" ? "green" : "blue"}>
                                {rawDataLead.lead_source_child}
                            </Tag>
                        )}
                    </span>
                }
            >
                <Descriptions
                    bordered
                    size="small"
                    column={1}
                    items={toRawEntries(rawDataLead?.response).map(({ key, value }) => ({
                        key,
                        label: key,
                        children: <span style={{ wordBreak: "break-word" }}>{value}</span>,
                    }))}
                />
            </Modal>

            <WhatsAppConversationDrawer
                open={Boolean(conversationLead)}
                onClose={() => setConversationLead(null)}
                companyId={activeCompany.company_id}
                companyType={activeCompany.company_type}
                leadResponseId={conversationLead?.id ?? null}
                title={`WhatsApp · ${conversationLead?.LeadName || ""}`}
            />
        </div>
    );
};

export default LeadBankTab;
